import { Close } from "@mui/icons-material";
import { Button, Checkbox, FormControlLabel, Modal } from "@mui/material";
import React, { useCallback, useContext, useState } from "react";

import { ReactComponent as BulbIcon } from "assets/icon-bulb.svg";
import { UserGuides } from "constants/constants";
import {
  SLAPSHOT_MOTION_VECTORS_GUIDE_URL,
  SMART_VECTORS_GUIDE_VIDEO_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";

import styles from "./TrackingOutputGuideModal.module.scss";

type Props = {
  open: boolean;
  onClose: () => void;
  /** After persisting tip prefs; e.g. start Add ROI annotation flow. */
  onGotIt?: () => void;
};

const SmartVectorsOutputGuideModal = ({ open, onClose, onGotIt }: Props) => {
  const user = useContext(UserContext);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleDismiss = useCallback(() => {
    setDontShowAgain(false);
    onClose();
  }, [onClose]);

  const handleGotIt = useCallback(() => {
    if (dontShowAgain) {
      user?.setPreferences("tips", UserGuides.SmartVectorsOutputGuide, true);
    }
    setDontShowAgain(false);
    onGotIt?.();
    onClose();
  }, [dontShowAgain, onClose, onGotIt, user]);

  const openExternalGuide = useCallback(() => {
    window.open(SLAPSHOT_MOTION_VECTORS_GUIDE_URL, "_blank");
  }, []);

  return (
    <Modal open={open} onClose={handleDismiss} className={styles.modal}>
      <div className={styles.svGuideModalWide}>
        <div className={styles.imageContainer}>
          {SMART_VECTORS_GUIDE_VIDEO_URL ? (
            <video
              className={styles.svGuideVideo}
              src={SMART_VECTORS_GUIDE_VIDEO_URL}
              autoPlay
              loop
              muted
              playsInline
            />
          ) : (
            <div className={styles.svVideoBlank} aria-hidden />
          )}
        </div>

        <div className={styles.svGuideRightColumn}>
          <div className={styles.innerContainer}>
            <h3 className={styles.title}>How to add a region of interest</h3>

            <div className={styles.list}>
              <div className={styles.listItem}>
                <h5>1. Select your keyframe</h5>
                <p>Select an optimal keyframe</p>
              </div>
              <div className={styles.listItem}>
                <h5>2. Mark region of interest</h5>
                <p>
                  Use our annotation tools to mark out the region of interest.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.svGuideModalDescription} />

          <div className={styles.svGuideModalActions}>
            <Button className={styles.okBtn} onClick={handleGotIt}>
              Got it!
            </Button>

            <div className={styles.svGuideAddRoiRow}>
              <div className={styles.svGuideLinkBox}>
                <div className={styles.svGuideLinkRow}>
                  <BulbIcon className={styles.svBulbIcon} aria-hidden />
                  <span className={styles.svGuideLinkRest}>
                    <button
                      type="button"
                      className={styles.svGuideLinkCta}
                      onClick={openExternalGuide}
                    >
                      Click here
                    </button>{" "}
                    to view our step by step guide on generating motion vectors.
                  </span>
                </div>
              </div>
            </div>

            <FormControlLabel
              className={styles.svDontShowAgainLabel}
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  sx={{
                    color: "#ffffff80",
                    "&.Mui-checked": { color: "#6565ec" },
                  }}
                />
              }
              label="Don't show this again."
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SmartVectorsOutputGuideModal;
